/**
 * OAuth2/OIDC error codes and parsing for Ory Hydra.
 * 
 * Source: https://github.com/ory/fosite/blob/master/errors.go
 * Spec: RFC 6749 (OAuth 2.0), RFC 6750 (Bearer Token), OpenID Connect Core 1.0
 */

import type { FlowError } from '@ory/client/api';

/** Standard OAuth2/OIDC error codes from RFC 6749, RFC 6750, and OpenID Connect. */
export type OAuth2ErrorCode =
  // Authorization Endpoint Errors (RFC 6749 Section 4.1.2.1)
  | 'invalid_request'              // Request is missing a required parameter, includes an invalid parameter value, includes a parameter more than once, or is otherwise malformed
  | 'unauthorized_client'          // Client is not authorized to request an authorization code using this method
  | 'access_denied'                // Resource owner or authorization server denied the request
  | 'unsupported_response_type'    // Authorization server does not support obtaining an authorization code using this method
  | 'invalid_scope'                // Requested scope is invalid, unknown, or malformed
  | 'server_error'                 // Authorization server encountered an unexpected condition that prevented it from fulfilling the request
  | 'temporarily_unavailable'      // Authorization server is currently unable to handle the request due to temporary overloading or maintenance

  // Token Endpoint Errors (RFC 6749 Section 5.2)
  | 'invalid_client'               // Client authentication failed (e.g., unknown client, no client authentication included, or unsupported authentication method)
  | 'invalid_grant'                // Provided authorization grant (e.g., authorization code, resource owner credentials) or refresh token is invalid, expired, revoked, does not match the redirection URI used in the authorization request, or was issued to another client
  | 'unsupported_grant_type'       // Authorization grant type is not supported by the authorization server

  // Resource Access Errors (RFC 6750 Section 3.1)
  | 'invalid_token'                // Access token provided is expired, revoked, malformed, or invalid for other reasons
  | 'insufficient_scope'           // Request requires higher privileges than provided by the access token

  // OpenID Connect Specific Errors
  | 'interaction_required'         // Authorization server requires end-user interaction of some form to proceed
  | 'login_required'               // Authorization server requires end-user authentication
  | 'account_selection_required'   // End-user is required to select a session at the authorization server
  | 'consent_required'             // Authorization server requires end-user consent
  | 'invalid_request_uri'          // request_uri in the authorization request returns an error or contains invalid data
  | 'invalid_request_object'       // request parameter contains an invalid Request Object
  | 'request_not_supported'        // Provider does not support use of the request parameter
  | 'request_uri_not_supported'    // Provider does not support use of the request_uri parameter
  | 'registration_not_supported'   // Provider does not support use of the registration parameter

  // Ory Hydra/Fosite Additional Errors
  | 'token_inactive'               // Token is not active
  | 'authorization_pending'        // Authorization request is still pending
  | 'slow_down'                    // Client should slow down polling
  | 'expired_token'                // Token has expired
  | 'unsupported_token_type'       // Token type is not supported
  | 'invalid_dpop_proof'           // DPoP proof is invalid
  | 'use_dpop_nonce'               // Server requires a DPoP nonce

  // Fallback for unknown error codes (Hydra may return additional codes)
  | string;

/** Raw OAuth2 error parameters from URL query string. Use `parseOAuth2Error()` to transform. */
export interface OAuth2ErrorParams {
  error: OAuth2ErrorCode;
  error_description?: string;
  error_uri?: string;
  error_hint?: string;
  error_debug?: string;
  status_code?: number;
  state?: string;
}

const getHttpStatusText = (code: number): string => {
  const statusTexts: Record<number, string> = {
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    410: 'Gone',
    422: 'Unprocessable Entity',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    504: 'Gateway Timeout',
  };

  return statusTexts[code] ?? `HTTP ${code}`;
};

/**
 * Parses OAuth2 error from URL parameters into FlowError-compatible structure.
 * 
 * Returns null if no error parameters present.
 */
export const parseOAuth2Error = (
  searchParams: URLSearchParams | { get(key: string): string | null }
): FlowError | null => {
  const errorCode = searchParams.get('error');

  if (!errorCode) {
    return null;
  }

  const statusCodeStr = searchParams.get('status_code');
  const statusCode = statusCodeStr ? parseInt(statusCodeStr, 10) : undefined;

  // Transform OAuth2 parameters into FlowError shape for unified error handling
  return {
    id: '', // FlowError requires id field - use empty string to mark as OAuth2 error
    error: {
      code: statusCode,
      id: errorCode,
      message: searchParams.get('error_description') ?? undefined,
      reason: searchParams.get('error_hint') ?? undefined,
      status: statusCode ? getHttpStatusText(statusCode) : undefined,
      debug: searchParams.get('error_debug') ?? undefined,
      request: undefined,
    },
  };
};
